//@flow
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { localize } from "redux-i18n";
import classNames from "classnames";
import config from "@config";
import type { State } from "@store/reducers";
import { ReassignStaffGroupModal, AddEditStaffGroupModal } from "@modules/service/components";
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Pagination,
  Icons,
  GSGrid,
  View,
  Text,
} from "@components";
import type { OptionType } from "@components";
import * as serviceSelectors from "@modules/service/selectors";
import actionCreator from "@modules/service/actionCreator";
import { toTableSort } from "@transformers/sort";
import type { PaginationExtendedType } from "@types";
import type { StaffGroup } from "@modules/service/reducer/staff-groups/types";

type PropsFromState = $Exact<{
  modalDisableVisible: boolean,
  modalEnableVisible: boolean,
  modalReassignVisible: boolean,
  pagination: PaginationExtendedType,
  staffGroups: Array<StaffGroup>,
  totalStaffGroups: Array<OptionType>,
  staffGroupName: ?string,
  totalElements: number,
  numberOfEmployees: ?number,
  isEditStaffGroupModalVisible: boolean,
  formData: Object,
  staffGroupId: ?number,
  sort: ?Object,
}>;

type PropsFromDispatch = $Exact<{
  onToggleDisableStaffGroupModal: typeof actionCreator.ToggleDisableStaffGroupModal,
  onToggleReassignStaffGroupModal: typeof actionCreator.ToggleReassignStaffGroupModal,
  onDismissDisableStaffGroupHandler: typeof actionCreator.ToggleDisableStaffGroupModal,
  onDismissEnableStaffGroupHandler: typeof actionCreator.ToggleEnableStaffGroupModal,
  onDisableStaffGroupHandler: typeof actionCreator.UpdateStaffGroup,
  onEnableStaffGroupHandler: typeof actionCreator.EnableStaffGroup,
  onReassignStaffGroup: typeof actionCreator.ReassignStaffGroup,
  onDismissReassignStaffGroupHandler: typeof actionCreator.ToggleReassignStaffGroupModal,
  onShowReassignToNewStaffGroup: typeof actionCreator.ShowReassignStaffGroupModal,
  onEditStaffGroupHandler: typeof actionCreator.ToggleEditStaffGroupModal,
  onDismissEditStaffGroupHandler: typeof actionCreator.ToggleEditStaffGroupModal,
  onEditStaffGroup: typeof actionCreator.EditStaffGroup,
  onSort: typeof actionCreator.SetStaffGroupsSort,
  getTotalStuffGroups: typeof actionCreator.GetTotalStaffGroups,
}>;

export type StaffGroupsListType = {
  filter: null | boolean,
  modalDisableVisible: boolean,
  modalEnableVisible: boolean,
  schema: Object,
  staffGroupId: number,
  sort: [],
  formData: Object,
  modalReassignVisible: boolean,
  isEditStaffGroupModalVisible: boolean,
  staffGroupName: string,
  numberOfEmployees: number,
  totalElements: number,
  totalElements: number,
  onShowReassignToNewStaffGroup: Function,
  fetchStaffGroups: Function,
  t: Function,
  staffGroups: [],
  clientId: number,
  pagination: {
    pageNumber: number,
    totalPages: number,
  },
  ...PropsFromDispatch,
  ...PropsFromState,
};

export function StaffGroupsList(props: StaffGroupsListType) {
  const [contextMenuPosition, setContextMenuPosition]: [
    {
      top: number,
      left: number,
    },
    Function,
  ] = useState(null);
  function handleShowContextOnClick(row, { pageX, pageY }) {
    setContextMenuPosition({ top: pageY, left: pageX });
  }

  function toggleDisableStaffGroupModal({ id, name, numberEmployees }) {
    return function() {
      props.onToggleDisableStaffGroupModal({
        name,
        numberEmployees,
        staffGroupId: id,
        visible: true,
      });
    };
  }

  function toggleReassignStaffGroupModal(row: Object) {
    return function() {
      props.getTotalStuffGroups({
        limit: props.totalElements,
        clientId: props.clientId,
      });
      props.onToggleReassignStaffGroupModal({
        name: row.name,
        numberEmployees: row.numberEmployees,
        staffGroupId: row.id,
        visible: true,
      });
    };
  }

  function disableStaffGroupHandler(bool, staffGroupId) {
    return function() {
      props.onDisableStaffGroupHandler({
        unassign: bool,
        staffGroupId: props.staffGroupId || staffGroupId,
        clientId: props.clientId,
        isEnabled: props.filter,
      });
    };
  }

  function reassignStaffGroup() {
    props.onReassignStaffGroup({
      unassign: false,
      staffGroupId: props.staffGroupId,
      clientId: props.clientId,
    });
  }

  function enableStaffGroupHandler(staffGroupId) {
    return function() {
      props.onEnableStaffGroupHandler({
        staffGroupId,
        clientId: props.clientId,
        isEnabled: props.filter,
      });
    };
  }

  function editStaffGroupHandler(row: Object) {
    return function() {
      props.onEditStaffGroupHandler({
        formData: {
          name: row.name,
          staffGroupType: { id: row.staffGroupTypeId },
          id: row.id,
        },
        staffGroupId: row.id,
        visible: true,
        isEnabled: props.filter,
      });
    };
  }

  function onEditStaffGroup(formData) {
    props.onEditStaffGroup({
      formData,
      clientId: props.clientId,
      staffGroupId: props.staffGroupId,
      isEnabled: props.filter,
    });
  }

  function onSort(sort) {
    props.onSort({
      clientId: props.clientId,
      sort,
    });
  }

  function handleShowReassignModal() {
    props.onShowReassignToNewStaffGroup();
  }

  const columns = [
    {
      name: props.t("ID"),
      key: "id",
    },
    {
      name: props.t("STAFF_GROUP"),
      key: "name",
      align: "left",
    },
    {
      name: props.t("DATE_ADDED"),
      renderCell: ({ row }: Object) => GSGrid.render.date(row.createdAt),
    },
    {
      name: props.t("TYPE"),
      align: "left",
      renderCell: ({ row }: Object) => row.staffGroupType.name,
    },
    {
      name: props.t("EMPLOYEES"),
      key: "numberEmployees",
      width: "min-content",
    },
    {
      name: props.t("STATUS"),
      renderCell: ({ row }: Object) =>
        GSGrid.render.statusPill(props.t(row.isEnabled ? "ACTIVE" : "INACTIVE")),
    },
    {
      name: "ACTION_MENU",
      contextMenuConfig: (row: Object) => {
        const config = [];

        if (row.numberEmployees)
          config.push({
            label: props.t("TRANSFER"),
            icon: "social_distance",
            onClick: toggleReassignStaffGroupModal(row),
          });
        config.push({
          label: props.t("EDIT"),
          icon: "edit",
          onClick: editStaffGroupHandler(row),
        });
        config.push({
          label: row.isEnabled ? props.t("DISABLE") : props.t("ENABLE"),
          icon: row.isEnabled ? "toggle_off" : "toggle_on",
          onClick: row.isEnabled
            ? disableStaffGroupHandler(false, row.id)
            : enableStaffGroupHandler(row.id),
        });
        return config;
      },
    },
  ];
  return (
    <View>
      {/* TODO Investigate later if it should be sortable */}
      <GSGrid rows={props.staffGroups} columns={columns} />

      {props.pagination && (
        <Pagination
          className="my-3 d-flex flex-fill justify-content-center"
          currentPage={props.pagination.pageNumber}
          totalPages={props.pagination.totalPages}
          onChange={props.fetchStaffGroups}
        />
      )}
      {/* Disable Modal */}
      {props.modalDisableVisible && (
        <Modal className="min-modal-width">
          <ModalHeader
            toggle={props.onDismissDisableStaffGroupHandler}
            className="d-flex justify-content-between"
          >
            {props.t("ATTENTION")}
          </ModalHeader>
          <ModalBody>
            <Text
              sm={{ size: 10, offset: 1 }}
              className="text-center"
              text={props.t("DISABLE_STAFF_GROUP_WARNING", {
                staffGroupName: props.staffGroupName,
              })}
            />
          </ModalBody>
          <ModalFooter>
            <Button
              color="secondary"
              onClick={props.onDismissDisableStaffGroupHandler}
              data-dismiss="modal"
            >
              {props.t("CANCEL")}
            </Button>
            <Button color="warning" onClick={disableStaffGroupHandler(false)}>
              {props.t("DISABLE_STAFF_GROUP")}
            </Button>
            <Button color="danger" onClick={disableStaffGroupHandler(true)}>
              {props.t("REMOVE_THIS_ASSIGNMENT")}
            </Button>
            <Button onClick={handleShowReassignModal}>{props.t("REASSIGN_TO_NEW_GROUP")}</Button>
          </ModalFooter>
        </Modal>
      )}
      {props.totalStaffGroups && props.modalReassignVisible && (
        <ReassignStaffGroupModal
          onDismissHandler={props.onDismissReassignStaffGroupHandler}
          onSubmit={reassignStaffGroup}
          numberOfEmployees={props.numberOfEmployees}
          staffGroupName={props.staffGroupName}
          options={props.totalStaffGroups}
        />
      )}
      {props.schema && props.isEditStaffGroupModalVisible && (
        <AddEditStaffGroupModal
          onDismissHandler={props.onDismissEditStaffGroupHandler}
          onSubmit={onEditStaffGroup}
          schema={props.schema}
          formData={props.formData}
          title={props.t("EDIT_STAFF_GROUP")}
          submitButtonIcon={"save"}
          submitButtonText={props.t("SAVE")}
        />
      )}
    </View>
  );
}

const mapStateToProps = (state: State): PropsFromState => ({
  modalDisableVisible: serviceSelectors.disableStaffGroupsModalVisible(state),
  modalEnableVisible: serviceSelectors.enableStaffGroupsModalVisible(state),
  modalReassignVisible: serviceSelectors.reassignStaffGroupsModalVisible(state),
  pagination: serviceSelectors.getStaffGroupsPagination(state),
  staffGroups: serviceSelectors.getStaffGroups(state),
  totalStaffGroups: serviceSelectors.getTotalStaffGroupList(state),
  staffGroupName: serviceSelectors.staffGroupName(state),
  totalElements: serviceSelectors.getStaffGroupTotalElements(state),
  numberOfEmployees: serviceSelectors.getNumberOfEmployees(state),
  isEditStaffGroupModalVisible: serviceSelectors.getIsEditStaffGroupModalVisible(state),
  formData: serviceSelectors.getStaffGroupFormData(state),
  staffGroupId: serviceSelectors.getStaffGroupId(state),
  sort: serviceSelectors.getStaffGroupsSort(state),
});

const mapDispatchToProps: PropsFromDispatch = {
  onToggleDisableStaffGroupModal: actionCreator.ToggleDisableStaffGroupModal,
  onToggleReassignStaffGroupModal: actionCreator.ToggleReassignStaffGroupModal,
  onDismissDisableStaffGroupHandler: actionCreator.ToggleDisableStaffGroupModal,
  onDismissEnableStaffGroupHandler: actionCreator.ToggleEnableStaffGroupModal,
  onDisableStaffGroupHandler: actionCreator.UpdateStaffGroup,
  onEnableStaffGroupHandler: actionCreator.EnableStaffGroup,
  onReassignStaffGroup: actionCreator.ReassignStaffGroup,
  onDismissReassignStaffGroupHandler: actionCreator.ToggleReassignStaffGroupModal,
  onShowReassignToNewStaffGroup: actionCreator.ShowReassignStaffGroupModal,
  onEditStaffGroupHandler: actionCreator.ToggleEditStaffGroupModal,
  onDismissEditStaffGroupHandler: actionCreator.ToggleEditStaffGroupModal,
  onEditStaffGroup: actionCreator.EditStaffGroup,
  onSort: actionCreator.SetStaffGroupsSort,
  getTotalStuffGroups: actionCreator.GetTotalStaffGroups,
};

export default connect(mapStateToProps, mapDispatchToProps)(localize()(StaffGroupsList));
